import { minutesToTime } from "@/pages/eaf/data-entry/utils/durationHelpers";
import { dxtToLocalServerTime, useContextMenuMESF } from "@dexteel/mesf-core";
import ListIcon from "@mui/icons-material/List";
import { Button, Grid2 as Grid, IconButton, Tooltip } from "@mui/material";
import { ColDef } from "ag-grid-community";
import { useEffect, useMemo } from "react";
import { LinkCellRenderer } from "../../../../../controls/ag-grid/components/ButtonCellRenderer";
import { statusCodeFormatter } from "../../../../../utils/heats-status-formatter";
import { useGridStyles } from "../../../../caster/styles/gridStyles";
import { useHeatsByShiftContext } from "../../context/HeatsByShiftContext";
import { useLadleContext } from "../../context/LadlesContext";
import { HeatStatusTag } from "../HeatStatusTag";
import { useHeatsOptionsFunctions } from "./useHeatsOptionsFunctions";

type Props = {
	setHeatIdForEdit: Function;
	setModalEditHeat: Function;
	setHeatIdForDelete: Function;
	setModalDeleteHeat: Function;
	setModalCreateHeat: Function;
	goToDataEntry: Function;
	goToEAFDataEntry: Function;
	goToHeatReport: Function;
};

export const useTableData = ({
	setHeatIdForEdit,
	setModalEditHeat,
	setHeatIdForDelete,
	setModalDeleteHeat,
	setModalCreateHeat,
	goToDataEntry,
	goToEAFDataEntry,
	goToHeatReport,
}: Props) => {
	const {
		state: { heats },
	} = useHeatsByShiftContext();
	const {
		state: { ladles },
	} = useLadleContext();

	const classes = useGridStyles();

	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const { getMenuOptions } = useHeatsOptionsFunctions({
		setHeatIdForEdit,
		setModalEditHeat,
		setHeatIdForDelete,
		setModalDeleteHeat,
		setModalCreateHeat,
	});

	useEffect(() => {
		registerConfig({
			id: "meltshop-heats",
			getOptions: getMenuOptions,
		});
	}, []);

	const columnData: ColDef[] = [
		{
			headerName: "Heat",
			field: "HeatName",
			flex: 1,
			minWidth: 74,
		},
		{
			headerName: "Status",
			field: "Status",
			flex: 1,
			minWidth: 150,
			maxWidth: 150,
			cellRenderer: (params: any) => {
				return <HeatStatusTag status={params.value} />;
			},
		},
		{
			headerName: "Start",
			flex: 1,
			minWidth: 70,
			valueGetter: (params) =>
				dxtToLocalServerTime(`${params.data.Start}`, "HH:mm"),
			cellRenderer: (params: any) => {
				if (params.data.HeatEAFDELink)
					return <LinkCellRenderer {...params} linkProperty="HeatEAFDELink" />;
				else return <span style={{ color: "black" }}>{params.value}</span>;
			},
		},
		{
			headerName: "Tap Time",
			flex: 1,
			minWidth: 80,
			valueGetter: (params) =>
				dxtToLocalServerTime(`${params.data.TapTime}`, "HH:mm"),
		},
		{
			headerName: "Ladle",
			field: "Ladle",
			flex: 1,
			minWidth: 70,
		},
		{
			headerName: "Ladle Open",
			flex: 1,
			minWidth: 94,
			valueGetter: (params) =>
				dxtToLocalServerTime(`${params.data.LadleOpen}`, "HH:mm"),
			cellRenderer: (params: any) => {
				if (params.data.HeatNameLink)
					return <LinkCellRenderer {...params} linkProperty="HeatNameLink" />;
				else return <span style={{ color: "black" }}>{params.value}</span>;
			},
		},
		{
			headerName: "Ladle Close",
			flex: 1,
			minWidth: 96,
			valueGetter: (params) =>
				dxtToLocalServerTime(`${params.data.LadleClose}`, "HH:mm"),
		},
		{
			headerName: "Grade",
			field: "Grade",
			flex: 1,
			minWidth: 79,
		},
		{
			headerName: "Last Sample",
			field: "LastSample",
			flex: 1,
			minWidth: 90,
			wrapHeaderText: true,
		},
		{
			headerName: "EAF Time",
			flex: 1,
			minWidth: 90,
			field: "EAFTime",
		},
		{
			headerName: "Cast Time",
			flex: 1,
			minWidth: 90,
			field: "CastTime",
		},
		{
			headerName: "Mold Size",
			field: "MoldSizeName",
			flex: 1,
			minWidth: 102,
		},
		{
			headerName: "Order",
			field: "HeatOrder",
			flex: 1,
			minWidth: 79,
		},
		{
			headerName: "Tons",
			field: "CastTons",
			flex: 1,
			minWidth: 74,
			valueGetter: (params) => {
				return params.data.CastTons ? params.data.CastTons.toFixed(1) : "";
			},
		},
		{
			headerName: "",
			flex: 1,
			minWidth: 180,
			field: "links",
			cellRenderer: (params: any) => {
				return (
					<div className={classes.buttons}>
						<Grid container justifyContent="center" alignItems="center">
							<Grid size={{ xs: 4 }} style={{ padding: "0" }}>
								<Tooltip title="See in EAF Data Entry" arrow>
									<Button
										fullWidth
										aria-label="EAF Page"
										disabled={!params.data.HeatEAFDELink}
										onClick={() => {
											goToEAFDataEntry(params.data.HeatEAFDELink);
										}}
									>
										<span
											className="material-icons"
											style={{
												color: !params.data.HeatEAFDELink
													? "#bababa"
													: "#3f51b5",
											}}
										>
											thermostat
										</span>
									</Button>
								</Tooltip>
							</Grid>
							<Grid size={{ xs: 4 }} style={{ padding: "0" }}>
								<Tooltip title="See in Caster Data Entry" arrow>
									<Button
										fullWidth
										aria-label="Caster Data Entry"
										disabled={!params.data.HeatNameLink}
										onClick={() => {
											goToDataEntry(params.data.HeatNameLink);
										}}
									>
										<span
											className="material-icons"
											style={{
												color: !params.data.HeatNameLink
													? "#bababa"
													: "#3f51b5",
											}}
										>
											memory
										</span>
									</Button>
								</Tooltip>
							</Grid>
							<Grid size={{ xs: 4 }} style={{ padding: "0" }}>
								<Tooltip title="See in Lab Report" arrow>
									<Button
										fullWidth
										disabled={!params.data.HeatLABLink}
										aria-label="Lab Page"
										onClick={() => {
											goToHeatReport(params.data.HeatLABLink);
										}}
									>
										<span
											className="material-icons"
											style={{
												color: !params.data.HeatLABLink ? "#bababa" : "#3f51b5",
											}}
										>
											science
										</span>
									</Button>
								</Tooltip>
							</Grid>
						</Grid>
					</div>
				);
			},
		},
		{
			field: "Actions",
			flex: 2,
			minWidth: 60,
			maxWidth: 60,
			cellRenderer: (params: any) => {
				return (
					<div className={classes.buttons}>
						<Grid
							size={{ xs: 6 }}
							container
							justifyContent="center"
							alignItems="center"
							style={{ padding: "0" }}
						>
							<Button
								fullWidth
								aria-label="Options"
								title="Options"
								style={{ cursor: "pointer" }}
								size="large"
								onClick={(e) =>
									showContextMenu(e, params.data, "meltshop-heats")
								}
							>
								<ListIcon
									fontSize="inherit"
									color="primary"
									style={{ cursor: "pointer", height: "20px", width: "20px" }}
								/>
							</Button>
						</Grid>
					</div>
				);
			},
			headerName: " ",
			sortable: false,
		},
	];
	const defaultColDef: ColDef = {
		flex: 1,
		resizable: true,
		suppressHeaderMenuButton: true,
	};

	const rowData = useMemo(() => {
		return heats.map(
			({
				ProcessId,
				HeatId,
				HeatName,
				Start,
				StartTap,
				HeatNameLink,
				HeatEAFDELink,
				HeatLABLink,
				LadleId,
				LadleOpen,
				LadleClose,
				GradeName,
				HeatStatusCode,
				LastSample,
				MoldSizeName,
				EAFTime,
				CastTime,
				HeatOrder,
				CastTons,
			}) => {
				const Ladle = ladles.find((l) => l.LadleId === LadleId);
				return {
					ProcessId,
					HeatName: HeatName,
					HeatNameLink: HeatNameLink,
					HeatEAFDELink: HeatEAFDELink,
					HeatLABLink,
					Start: Start,
					TapTime: StartTap,
					Ladle: Ladle?.LadleName,
					LadleOpen: LadleOpen,
					LadleClose: LadleClose,
					Grade: GradeName,
					Status: statusCodeFormatter(HeatStatusCode),
					HeatId: HeatId,
					LastSample: LastSample,
					MoldSizeName,
					EAFTime: EAFTime ? minutesToTime(EAFTime) : null,
					CastTime: CastTime ? minutesToTime(CastTime) : null,
					HeatOrder,
					CastTons,
					Id: HeatId,
				};
			},
		);
	}, [heats]);

	return { rowData, columnData, defaultColDef };
};
